import { showNotification } from '@mantine/notifications';
import type { ActivityLogOut } from '@repo/api-codegen';
import { trimMarkdown } from '@repo/common/utils';
import dayjs from 'dayjs';
import { sortBy } from 'lodash-es';
import {
	makeAutoObservable,
	ObservableMap,
	onBecomeObserved,
	onBecomeUnobserved,
	runInAction,
} from 'mobx';
import type {
	IApiListResponse,
	IQuestion,
	IQuestionReply,
	IUser,
} from '../../api';
import type { QuestionPermissions } from '../../api/hooks/question/permissions';
import type { SentimentType } from '../../components/AIAssistant/AIFeedbackModal/constants';
import { isSecodaAIUser } from '../../components/AIAssistant/utils';
import { updateReactionArray } from '../../components/InlineEmojiPicker/utils';
import type {
	PartialWithoutId,
	WithOnlyIdRequired,
} from '../../lib/typescript';
import { QuestionReplyType } from './types';

class QuestionReplyStore {
	questionStore: QuestionStore;
	reply: IQuestionReply;
	timeoutId: NodeJS.Timeout | null = null;
	editing: boolean = false;
	editingDefinition: string | null = null;
	editingEditorKey: number = 0;
	editingLoading: boolean = false;
	showSteps: boolean = false;

	constructor(questionStore: QuestionStore, reply: IQuestionReply) {
		makeAutoObservable(this, undefined, { autoBind: true });

		this.questionStore = questionStore;
		this.reply = reply;

		if (reply.ai_status === 'pending' || reply.ai_status === 'running') {
			this.startPooling();
		}

		// stop polling when the reply is no longer observed
		onBecomeUnobserved(this, 'reply', () => this.stopPooling());
	}

	async refresh() {
		const updatedReply = await this.questionStore.fetchQuestionReply(
			this.reply.id
		);

		if (updatedReply) {
			runInAction(() => {
				this.reply = updatedReply;
			});

			if (
				['completed', 'failed'].includes(updatedReply.ai_status ?? 'failed')
			) {
				this.stopPooling();
				return;
			}
		}

		this.startPooling();
	}

	startPooling() {
		this.timeoutId = setTimeout(() => {
			this.refresh();
		}, 1000);
	}

	stopPooling() {
		if (this.timeoutId) {
			clearTimeout(this.timeoutId);
			this.timeoutId = null;
		}
	}

	async update(data: Partial<IQuestionReply>) {
		try {
			this.reply = await this.questionStore.updateQuestionReply({
				...data,
				id: this.reply.id,
			});
		} catch {
			showNotification({
				title: 'Error',
				message: 'Failed to update reply',
				color: 'red',
			});
		}
	}

	async startEditing() {
		this.editing = true;
		this.editingDefinition = this.reply.definition ?? '';
	}

	async cancelEditing() {
		this.editing = false;
		this.editingDefinition = null;
		this.editingEditorKey += 1;
	}

	async setEditingDefinition(definition: string) {
		this.editingDefinition = definition;
	}

	async saveEditing() {
		if (!this.editingDefinition?.length) {
			return;
		}

		this.editingLoading = true;

		const oldDefinition = this.reply.definition;

		try {
			const newDefinition = trimMarkdown(this.editingDefinition ?? '');

			// optimistic update
			this.reply.definition = newDefinition;
			this.editing = false;
			await this.update({
				definition: newDefinition,
				id: this.reply.id,
			});
			this.editingDefinition = null;
			this.editingEditorKey += 1;
		} catch {
			showNotification({
				title: 'Error',
				message: 'Failed to update reply',
				color: 'red',
			});

			// rollback
			this.reply.definition = oldDefinition;
			this.editing = true;
		} finally {
			this.editingLoading = false;
		}
	}

	async delete() {
		try {
			// delete optimistically
			this.questionStore.replies.delete(this.reply.id);
			await this.questionStore.deleteQuestionReply(this.reply.id);
		} catch {
			showNotification({
				title: 'Error',
				message: 'Failed to remove reply',
				color: 'red',
			});

			// rollback
			this.questionStore.replies.set(this.reply.id, this);
		}

		// refresh the list passively to bring in possible new replies
		this.questionStore.fetchReplies();
	}

	get userCanEdit() {
		// only the owner can edit the reply
		return this.reply.owners?.[0] === this.questionStore.user.id;
	}

	get userCanDelete() {
		return (
			this.reply.owners?.[0] === this.questionStore.user.id ||
			this.questionStore.permissions.canDelete
		);
	}

	async toggleReaction({ native }: { native: string }) {
		const existingReactions = this.reply.reactions ?? [];
		const reactions = updateReactionArray(
			existingReactions,
			native,
			this.questionStore.user.id
		);

		try {
			// optimistic update
			this.reply.reactions = reactions;

			await this.questionStore.updateQuestionReply({
				id: this.reply.id,
				reactions,
			});
		} catch {
			showNotification({
				title: 'Error',
				message: 'Failed to update reply',
				color: 'red',
			});

			// rollback
			this.reply.reactions = existingReactions;
		}
	}

	async toggleMarkAsAnswer() {
		const existingAcceptedAnswer = Array.from(
			this.questionStore.replies.values()
		).find((reply) => reply.reply.accepted_answer);

		try {
			// optimistic update
			this.reply.accepted_answer = !this.reply.accepted_answer;
			if (
				this.reply.accepted_answer &&
				existingAcceptedAnswer &&
				existingAcceptedAnswer.reply.id !== this.reply.id
			) {
				// unmark the existing accepted answer - backend will handle persistence for this automatically
				existingAcceptedAnswer.reply.accepted_answer = false;
			}

			await this.questionStore.updateQuestionReply({
				accepted_answer: this.reply.accepted_answer,
				id: this.reply.id,
			});
		} catch {
			showNotification({
				title: 'Error',
				message: 'Failed to update reply',
				color: 'red',
			});

			// rollback
			this.reply.accepted_answer = !this.reply.accepted_answer;

			if (
				!this.reply.accepted_answer &&
				existingAcceptedAnswer &&
				existingAcceptedAnswer.reply.id !== this.reply.id
			) {
				// rollback the unmarking of the existing accepted answer
				existingAcceptedAnswer.reply.accepted_answer = true;
			}
		}
	}

	toggleShowSteps() {
		this.showSteps = !this.showSteps;
	}

	get replyType() {
		const owner = this.reply.display_metadata?.owners?.[0];

		// Legacy AI replies does not use ai_status and are created by the default AI user. Might have ai_steps
		if (
			owner &&
			isSecodaAIUser(owner, this.questionStore.question.workspace_id) &&
			!this.reply.ai_status
		) {
			return QuestionReplyType.LegacyAi;
		}

		// New AI replies use ai_status (with structured response)
		if (this.reply.ai_status) {
			return QuestionReplyType.Ai;
		}

		return QuestionReplyType.User;
	}

	updateUserFeedback(feedback: SentimentType) {
		if (!this.reply.embedded_prompt) {
			return;
		}

		this.reply.embedded_prompt.user_feedback_raw = {
			sentiment: feedback,
		};
	}
}

interface QuestionStoreProps {
	user: IUser;
	question: IQuestion;
	replies: IQuestionReply[];
	replyInputInitialValue: string;
	permissions: QuestionPermissions;
	focusedReplyId?: string;
	updateQuestion: (data: Partial<IQuestion>) => Promise<IQuestion>;
	fetchQuestionReplyList: () => Promise<IApiListResponse<IQuestionReply>>;
	fetchQuestionReply: (id: string) => Promise<IQuestionReply>;
	createQuestionReply: (
		data: PartialWithoutId<IQuestionReply>
	) => Promise<IQuestionReply>;
	updateQuestionReply: (
		data: WithOnlyIdRequired<IQuestionReply>
	) => Promise<IQuestionReply>;
	deleteQuestionReply: (id: string) => Promise<IQuestionReply>;
	fetchActivityLogs: () => Promise<ActivityLogOut[]>;
}

export class QuestionStore {
	question: IQuestion;
	user: IUser;
	permissions: QuestionPermissions;
	focusedReplyId?: string;
	updateQuestion: (data: Partial<IQuestion>) => Promise<IQuestion>;
	fetchQuestionReply: (id: string) => Promise<IQuestionReply>;
	createQuestionReply: (
		data: PartialWithoutId<IQuestionReply>
	) => Promise<IQuestionReply>;
	updateQuestionReply: (
		data: WithOnlyIdRequired<IQuestionReply>
	) => Promise<IQuestionReply>;
	deleteQuestionReply: (id: string) => Promise<IQuestionReply>;
	fetchQuestionReplyList: () => Promise<IApiListResponse<IQuestionReply>>;
	replies: ObservableMap<string, QuestionReplyStore> = new ObservableMap();
	replyInputValue: string = '';
	replySubmitting: boolean = false;
	replyEditorKey: number = 0;
	activityLogs: ActivityLogOut[] = [];
	fetchActivityLogs: () => Promise<ActivityLogOut[]>;
	activityLogsTimeoutId: NodeJS.Timeout | null = null;
	activityLogsPollingMs: number = 1000;

	constructor({
		question,
		replies,
		user,
		replyInputInitialValue,
		permissions,
		focusedReplyId,
		updateQuestion,
		fetchQuestionReplyList,
		fetchQuestionReply,
		createQuestionReply,
		updateQuestionReply,
		deleteQuestionReply,
		fetchActivityLogs,
	}: QuestionStoreProps) {
		makeAutoObservable(this, undefined, { autoBind: true });

		this.question = question;
		this.user = user;
		this.replyInputValue = replyInputInitialValue;
		this.permissions = permissions;
		this.focusedReplyId = focusedReplyId;
		this.updateQuestion = updateQuestion;
		this.fetchQuestionReplyList = fetchQuestionReplyList;
		this.fetchQuestionReply = fetchQuestionReply;
		this.createQuestionReply = createQuestionReply;
		this.updateQuestionReply = updateQuestionReply;
		this.deleteQuestionReply = deleteQuestionReply;
		this.setReplies(replies);
		this.fetchActivityLogs = fetchActivityLogs;

		// Start polling only when activity logs are observed, and stop when unobserved
		onBecomeObserved(this, 'activityLogs', () => {
			this.loadActivityLogs();
			this.startActivityLogsPolling();
		});
		onBecomeUnobserved(this, 'activityLogs', () =>
			this.stopActivityLogsPolling()
		);

		// Auto-cleanup when the entire store becomes unobserved
		onBecomeUnobserved(this, 'question', () => {
			this.destroy();
		});
	}

	async fetchReplies() {
		const replies = await this.fetchQuestionReplyList();
		this.setReplies(replies.results ?? []);
	}

	async setReplies(replies: IQuestionReply[]) {
		const sortedReplies = sortBy(replies, 'created_at');

		const sortedIds = [];

		for (const reply of sortedReplies) {
			this.replies.set(reply.id, new QuestionReplyStore(this, reply));
			sortedIds.push(reply.id);
		}

		// remove any replies that are not in the sorted list (probably deleted remotely)
		for (const reply of this.replies.values()) {
			if (!sortedIds.includes(reply.reply.id)) {
				this.replies.delete(reply.reply.id);
			}
		}

		// Handle content-based reply matching as fallback
		if (this.focusedReplyId?.startsWith('content:')) {
			const targetContent = this.focusedReplyId.slice(8); // Remove "content:" prefix

			const matchingReply = sortedReplies.find((reply) => {
				if (!reply.definition) return false;

				// Try exact match first
				if (reply.definition.trim() === targetContent.trim()) {
					return true;
				}

				// Try matching without markdown formatting
				const cleanReplyDef = reply.definition
					.replace(/[*_]/g, '') // Remove bold/italic
					.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links: [text](url) -> text
					.trim();
				const cleanTargetContent = targetContent
					.replace(/[*_]/g, '') // Remove bold/italic
					.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links: [text](url) -> text
					.trim();

				return cleanReplyDef === cleanTargetContent;
			});

			if (matchingReply) {
				this.focusedReplyId = matchingReply.id;
			} else {
				// If no exact match, clear the focused reply to avoid errors
				this.focusedReplyId = undefined;
			}
		}
	}

	async setReplyInputValue(value: string) {
		this.replyInputValue = value;
	}

	async createReply() {
		if (!this.replyInputValue.length) {
			return;
		}

		runInAction(() => {
			this.replySubmitting = true;
		});

		try {
			const reply = await this.createQuestionReply({
				parent: this.question.id,
				definition: trimMarkdown(this.replyInputValue),
				owners: [this.user.id],
				teams: this.question.teams,
			});
			runInAction(() => {
				this.replies.set(reply.id, new QuestionReplyStore(this, reply));
				this.replyInputValue = '';
				this.replyEditorKey += 1;
				this.fetchReplies();
			});
		} catch {
			showNotification({
				title: 'Error',
				message: 'Failed to create reply',
				color: 'red',
			});
		} finally {
			runInAction(() => {
				this.replySubmitting = false;
			});
		}
	}

	async update(data: Partial<IQuestion>) {
		const previousQuestion = { ...this.question };

		try {
			// optimistic update
			this.question = { ...this.question, ...data };

			await this.updateQuestion({
				...data,
				id: this.question.id,
			});
		} catch {
			showNotification({
				title: 'Error',
				message: 'Failed to update question',
				color: 'red',
			});

			// rollback
			this.question = previousQuestion;
		}

		return this.question;
	}

	async toggleReaction({ native }: { native: string }) {
		const existingReactions = this.question.reactions ?? [];
		const reactions = updateReactionArray(
			existingReactions,
			native,
			this.user.id
		);

		try {
			// optimistic update
			this.question.reactions = reactions;

			await this.updateQuestion({
				id: this.question.id,
				reactions,
			});
		} catch {
			showNotification({
				title: 'Error',
				message: 'Failed to update question',
				color: 'red',
			});

			// rollback
			this.question.reactions = existingReactions;
		}
	}

	async loadActivityLogs() {
		const activityLogs = await this.fetchActivityLogs();

		runInAction(() => {
			this.activityLogs = activityLogs;
		});
	}

	startActivityLogsPolling() {
		this.stopActivityLogsPolling();
		this.activityLogsTimeoutId = setTimeout(() => {
			this.loadActivityLogs().finally(() => {
				// Continue polling regardless of success or failure
				this.startActivityLogsPolling();
			});
		}, this.activityLogsPollingMs);
	}

	stopActivityLogsPolling() {
		if (this.activityLogsTimeoutId) {
			clearTimeout(this.activityLogsTimeoutId);
			this.activityLogsTimeoutId = null;
		}
	}

	destroy() {
		this.stopActivityLogsPolling();
	}

	get activityLogsAndReplies(): Array<
		| { type: 'activity_log'; data: ActivityLogOut; date: Date }
		| { type: 'reply'; reply_id: string; date: Date }
	> {
		const list = [
			...(this.activityLogs.map((log) => ({
				type: 'activity_log',
				data: log,
				date: dayjs(log.logged_at).toDate(),
			})) as Array<{ type: 'activity_log'; data: ActivityLogOut; date: Date }>),
			...(Array.from(this.replies.values()).map((reply) => ({
				type: 'reply',
				reply_id: reply.reply.id,
				date: dayjs(reply.reply.created_at).toDate(),
			})) as Array<{ type: 'reply'; reply_id: string; date: Date }>),
		];

		return list.sort((a, b) => a.date.getTime() - b.date.getTime());
	}
}
